/* eslint-disable operator-assignment */
import { useEffect, useCallback, useState } from "react";
import { NotUndefined } from "object-hash";
import { useDispatch } from "react-redux";

import { useTypedSelector } from "../redux/store";

import { useModelSubscribe } from ".";

function useMountableGetAll<
	SubscribeOptions extends NotUndefined,
	Model extends { id: string | number },
>({
	subscribeOptions,
	loadingChange,
	idModal,
	isUseForRedax,
	onLoadingChange,
	onModelsChange,
	onMetadataChange,
	serviceGetAll,
	selector,
	setReduxModels,
	onClickBtnSeatch,
}: UseGetAll.Props<SubscribeOptions, Model>) {
	const dispatch = useDispatch();

	const INITIAL_CHUNK_SIZE = 50;
	const INITIAL_CHUNK_COUNT = 1;
	const INITIAL_OFFSET = 0;

	const reduxData = useTypedSelector(selector);

	const [initialIdModal] = useState(idModal);
	const [isEndReached, setIsEndReached] = useState(true);

	const setModelsForOnceModal = useCallback(
		(obj: any) => {
			dispatch(setReduxModels(obj));
		},
		[dispatch, setReduxModels],
	);

	const fetchData = useCallback(async () => {
		if (!initialIdModal) return;
		onLoadingChange?.(true);
		setIsEndReached(false);

		let data: Model[] = [];
		const chunkSize = INITIAL_CHUNK_SIZE;
		const chunkCount = INITIAL_CHUNK_COUNT;
		let currentOffset = INITIAL_OFFSET;

		const check = async () => {
			const promises: Promise<Model[]>[] = Array.from({
				length: chunkCount,
			}).map((_, index) => {
				const offset = currentOffset + index * chunkSize;
				const options = {
					...subscribeOptions,
					...(offset ? { offset } : {}),
					limit: chunkSize,
				};
				return serviceGetAll(options);
			});

			const chunkedResults = await Promise.all(promises);

			const validResults = chunkedResults.filter((res): res is Model[] =>
				Array.isArray(res),
			);

			const mergedItems = validResults.flatMap((res) => res ?? []);

			const existingIds = new Set(data.map((item) => item.id));
			const newItems = mergedItems.filter(
				(item) => !!item && !existingIds.has(item.id),
			);

			data = [...data, ...newItems];

			const isLastChunk = validResults.every(
				(res) => res.length < chunkSize,
			);

			if (!isLastChunk) {
				currentOffset = currentOffset + chunkSize * chunkCount;

				setModelsForOnceModal({
					[initialIdModal]: {
						subscribeOptions:
							subscribeOptions as useModelSubscribe.Options<any>,
						models: data,
						metadata: {
							pagination: {
								offset: 0,
								count: data.length || 0,
							},
							total: 0,
						},
					},
				});

				await check();
			}
		};

		await check();

		setModelsForOnceModal({
			[initialIdModal]: {
				subscribeOptions:
					subscribeOptions as useModelSubscribe.Options<any>,
				models: data,
				metadata: {
					pagination: {
						offset: 0,
						count: data.length || 0,
					},
					total: 0,
				},
			},
		});

		setIsEndReached(true);
		onLoadingChange?.(false);
	}, [
		initialIdModal,
		onLoadingChange,
		setModelsForOnceModal,
		subscribeOptions,
		serviceGetAll,
	]);

	useEffect(() => {
		if (
			isUseForRedax &&
			!loadingChange &&
			isEndReached &&
			onClickBtnSeatch > 0
		) {
			fetchData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onClickBtnSeatch]);

	useEffect(() => {
		if (isUseForRedax && initialIdModal && reduxData) {
			const transaction = reduxData?.[initialIdModal] ?? null;
			if (transaction) {
				onModelsChange?.(transaction.models as Model[]);
				if (transaction.metadata) {
					onMetadataChange?.(transaction.metadata);
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reduxData]);
}

declare namespace UseGetAll {
	export interface Props<SubscribeOptions extends NotUndefined, Model> {
		subscribeOptions: any;
		Service: useModelSubscribe.Service<SubscribeOptions, Model>;
		options?: useModelSubscribe.Options<Model>;
		idModal?: number;
		isUseForRedax?: boolean;
		loadingChange?: boolean;
		onLoadingChange?: (value: boolean) => void;
		onModelsChange?: (value: Model[]) => void;
		onMetadataChange?: (value: any) => void;
		serviceGetAll: (options: Record<string, unknown>) => Promise<Model[]>;
		selector: (state: any) => any;
		setReduxModels: (obj: any) => any;
		onClickBtnSeatch: number;
	}
}

export default useMountableGetAll;
